/**
 * EditorStore.fxHelpers.ts
 *
 * Pure helper functions for FX block creation logic,
 * extracted from EditorStore.tsx (S2).
 */

import type { FxBlock } from "./fxTypes";
import {
  FX_BLOCK_DEFAULT_CREATE_WINDOW_SEC,
  FX_BLOCK_DEFAULT_CREATE_WINDOW_EPSILON_SEC,
} from "./EditorStore.types";
import type { FxBlockTargetClipWindow } from "./EditorStore.types";

export function isDefaultFxCreateWindow(fxBlock: FxBlock): boolean {
  if (fxBlock.preserveDuration) return false;
  const requestedDuration = Math.max(0.05, fxBlock.end - fxBlock.start);
  return requestedDuration <= FX_BLOCK_DEFAULT_CREATE_WINDOW_SEC + FX_BLOCK_DEFAULT_CREATE_WINDOW_EPSILON_SEC;
}

export function resolveFxBlockCreationTargets(args: {
  fxBlock: FxBlock;
  clips: FxBlockTargetClipWindow[];
  selectedClipIds: string[];
  playhead: number;
}): FxBlockTargetClipWindow[] {
  const { fxBlock, clips, selectedClipIds, playhead } = args;
  const clipById = new Map(clips.map((clip) => [clip.id, clip]));
  const out: FxBlockTargetClipWindow[] = [];
  const pushById = (id: string) => {
    const clip = clipById.get(id);
    if (!clip) return;
    if (!Number.isFinite(clip.start) || !Number.isFinite(clip.end) || clip.end <= clip.start) return;
    if (out.some((item) => item.id === clip.id)) return;
    out.push(clip);
  };



  const selectedIds = selectedClipIds.filter(Boolean);
  if (selectedIds.length > 0) {
    for (const id of selectedIds) pushById(id);
    if (out.length > 0) return out;
  }

  const explicitTargetIds = Array.isArray(fxBlock.targetClipIds) ? fxBlock.targetClipIds : [];
  if (explicitTargetIds.length > 0) {
    for (const id of explicitTargetIds) pushById(id);
    if (out.length > 0) return out;
  }




  const activeClip = clips
    .filter((clip) => playhead + 1 / 240 >= clip.start && playhead <= clip.end + 1 / 240)
    .sort((a, b) => a.start - b.start)[0];
  if (activeClip) out.push(activeClip);
  return out;
}

export function resolveFxBlockCreationWindow(
  fxBlock: FxBlock,
  targetClip: FxBlockTargetClipWindow | null
): { start: number; end: number } {
  if (!targetClip || !isDefaultFxCreateWindow(fxBlock)) {
    return { start: fxBlock.start, end: fxBlock.end };
  }
  return { start: targetClip.start, end: targetClip.end };
}

export function resolveFxBlockCreationCandidates(args: {
  fxBlock: FxBlock;
  clips: FxBlockTargetClipWindow[];
  selectedClipIds: string[];
  playhead: number;
  idFactory?: () => string;
}): Array<Pick<FxBlock, "id" | "start" | "end" | "targetClipIds">> {
  const { fxBlock, clips, selectedClipIds, playhead } = args;
  const idFactory = args.idFactory ?? (() => crypto.randomUUID());
  const targets = resolveFxBlockCreationTargets({
    fxBlock,
    clips,
    selectedClipIds,
    playhead,
  });
  const shouldExpandDefaults = !fxBlock.preserveDuration && isDefaultFxCreateWindow(fxBlock);
  if (shouldExpandDefaults && targets.length > 1) {
    return targets.map((target, index) => ({
      id: index === 0 ? fxBlock.id : idFactory(),
      start: target.start,
      end: target.end,
      targetClipIds: [target.id],
    }));
  }

  const primaryTarget = targets[0] ?? null;
  const window = resolveFxBlockCreationWindow(fxBlock, primaryTarget);
  return [
    {
      id: fxBlock.id,
      start: window.start,
      end: window.end,
      targetClipIds: primaryTarget ? [primaryTarget.id] : fxBlock.targetClipIds,
    },
  ];
}
